import React, {useEffect, useRef, useState} from 'react';
import qs from 'query-string';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import {ControlButton, TscButtonType, Glyphs, TscThemeName, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import decode from 'entity-decode';
import Constants from '../../constants/Constants';
import cssConstants from '../../constants/cssConstants';
import Optional from '../../model/optional';
import config from '../../service/config';
import EditDetailsModal from './EditDetailsModal';
import MediaTitle from './MediaTitle';
import MoreMenu from './MoreMenu';
import MediaLocation from './MediaLocation';
import {UpdateWindowSizingState, windowSizingStore} from '../../context/windowSizingProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import ShareModal from '../modals/ShareModal';
import CreateCollectionModal from '../modals/CreateCollectionModal';
import ShareToCollectionModal from '../modals/ShareToCollectionModal';
import {conversationsStore} from '../../context/conversationsProvider';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore, updateMediaStateFromServer} from '../../context/mediaProvider';
import {migrationStore, pollInProgressMigrations} from '../../context/migrationProvider';
import {setupCSSHeightObserver} from '../../util/CSSHeightObserver';
import SparkleGlyph from '../../static/svg/SparkleGlyph16x16.svg';
import {useQueryParam, StringParam} from 'use-query-params';
import {IBaseLibrary} from '../../model/library';

// this calculation is done so the text lines up specifically with the text of the first management button
const contentPadding = `${parseFloat(cssConstants.viewPageMeasurements.managementTabHorizontalMargin) + parseFloat(cssConstants.viewPageMeasurements.managementTabHorizontalPadding)}rem`;

const isCompleteRecordingSessionKey = 'iscompleterecordingsession';

const mediaDetailsExpansionTransition = 250;

const MediaDetailsContainer = styled.div<{theme: TscThemeNames}>`
   display: flex;
   flex-direction: column;
   flex-shrink: 0;
   flex-grow: 1;
   align-self: stretch;
   padding: ${contentPadding};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   font-size: 1rem;
`;

const MediaActionWrapper = styled.div<{mediaTabsPanelOpen: boolean}>`
   @media screen and (max-width: ${props => props.mediaTabsPanelOpen ? cssConstants.mediaDetailsButtonTextBreakPointWithmediaTabsPanelOpen : cssConstants.mediaDetailsButtonTextBreakPoint}px) {
      button span:nth-child(2) {
         display: none;
      }
   }
`;

const MediaSpacedBar = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   width: 100%;
   max-width: ${cssConstants.contentWidth};
   margin: 0 auto;
`;

const MediaActions = styled.div`
   display: flex;
   flex-shrink: 0;
   height: 2rem;
`;

interface IEditDetailsTheme {
   [theme: string]: {
      color: string;
      backgroundColor: string;
      backgroundHoverColor: string;
      backgroundActiveColor: string;
      borderGradient: string;
      accentBorderColor: string;
      accentBackgroundColor: string;
      accentDropShadow: string;
   };
}
const editDetailsThemes = {
   [TscThemeName.dawn]: {
      color: TscStyles.color.text.dark,
      backgroundColor: '#F8F8F7',
      backgroundHoverColor: '#dad9db',
      backgroundActiveColor: '#dad9db',
      borderGradient: 'linear-gradient(137deg, rgba(248, 76, 200, 0.60) 0%, rgba(239, 130, 0, 0.50) 88.47%)',
      accentBorderColor: 'rgba(255, 255, 255, 0.75)',
      accentBackgroundColor: 'rgba(255, 255, 255, 0.50)',
      accentDropShadow: 'box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.10)'
   },
   [TscThemeName.dusk]: {
      color: TscStyles.color.text.light,
      backgroundColor: TscStyles.color.ui.dusk.mediumDark,
      backgroundHoverColor: '#444749',
      backgroundActiveColor: '#383b3e',
      borderGradient: 'linear-gradient(137deg, rgba(248, 76, 200, 0.80) 0%, rgba(239, 130, 0, 0.40) 88.47%)',
      accentBorderColor: 'rgba(255, 255, 255, 0.15)',
      accentBackgroundColor: 'rgba(21, 25, 28, 0.50)',
      accentDropShadow: 'box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.30)'
   }
} as IEditDetailsTheme;

const EditMediaButton = styled.button.attrs({'data-test-id': 'edit-details-button'})<{isDisabled: boolean; theme: TscThemeNames}>`
   position: relative;
   display: inline-flex;
   align-items: center;
   font-family: Figtree, sans-serif;
   font-size: 0.875rem;
   font-weight: 600;
   text-decoration: none;
   text-align: center;
   vertical-align: middle;
   outline: none;
   line-height: 2rem;
   height: 2rem;
   cursor: pointer;
   width: auto;
   max-width: 22em;
   padding: 0 .75rem;
   box-sizing: border-box;
   fill: ${props => editDetailsThemes[props.theme].color};
   color: ${props => editDetailsThemes[props.theme].color};
   border: solid 1px transparent;
   background: ${props => editDetailsThemes[props.theme].backgroundColor};
   background-clip: padding-box;
   border-radius: ${TscStyles.border.radius.md};
   ${props => props.isDisabled && 'opacity: 50%;'};
   pointer-events: ${props => props.isDisabled ? 'none' : 'auto'};

   &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
      margin: -0.0625rem;
      border-radius: inherit;
      ${props => !props.isDisabled && `background: ${editDetailsThemes[props.theme].borderGradient}`};
   }

   &::-moz-focus-inner {
      border: 0;
   }

   &:focus-visible {
      outline: none;
      &:after{
         content: '';
         display: block;
         position: absolute;
         top: 2px;
         right: 2px;
         bottom: 2px;
         left: 2px;
         border: 2px solid currentColor;
         border-radius: ${TscStyles.border.radius.sm};
      }
   }

   &:hover {
      background: ${props => editDetailsThemes[props.theme].backgroundHoverColor};
      box-shadow: 0 2px 3px ${TscStyles.color.global.boxShadow};
      text-decoration: none;

      &:before {
         background: none;
      }
   }

   &:active {
      box-shadow: inset 0 2px 3px ${TscStyles.color.global.boxShadow};
      background: ${props => editDetailsThemes[props.theme].backgroundActiveColor};
   }

   .editDetailsGlyph {
      display: inline-flex;
   }

   .ai-glyph {
      position: absolute;
      pointer-events: none;
      left: -10px;
      top: -10px;
      display: inline-flex;
      padding: 2px;
      justify-content: center;
      align-items: center;
      border-radius: ${TscStyles.border.radius.sm};
      border: 1px solid ${props => editDetailsThemes[props.theme].accentBorderColor};
      background: ${props => editDetailsThemes[props.theme].accentBackgroundColor};
      ${props => editDetailsThemes[props.theme].accentDropShadow};
      backdrop-filter: blur(3px);
   }

   .button-label {
      display: inline-block;
      margin-left: .5rem;
   }
`;

interface INormalizedParsedQuery {
   [key: string]: Optional<string | string[]>;
}
export const MediaDetailsBase: React.FC<IMediaDetailsProps & IStateMappedProps> = ({showMediaLocation, mediaDetailsIsExpanded, mediaTabsPanelOpen, Title, IsReady, IsVideo, OwnerLibrary, migrationInProgress}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
   const [showShareModal, setShowShareModal] = useState(false);
   const [showShareToCollectionModal, setShowShareToCollectionModal] = useState(false);
   const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
   const [view] = useQueryParam(Constants.queryParams.view, StringParam);

   const mediaSupportAIFeatures = () => config.featureSwitches.EnableAIMediaSummary && IsVideo;

   const removeIsCompleteRecordingSessionFromQueryString = (parsedQuery: INormalizedParsedQuery) => {
      if (location.href.includes('?')) {
         delete parsedQuery[isCompleteRecordingSessionKey];

         const baseUrl = location.href.split('?')[0];
         const updatedQueryString = qs.stringify(parsedQuery);
         const updatedUrl = updatedQueryString.length > 0 ? `${baseUrl}?${updatedQueryString}` : baseUrl;

         window.history.replaceState({}, window.document.title, updatedUrl);
      }
   };

   const detailsContainer = useRef<HTMLDivElement>(null);

   useEffect(() => {
      void pollInProgressMigrations();
   }, [migrationInProgress]);

   useEffect(() => {
      document.title = `${decode(Title) as string} - ${config.applicationFullName}`;
   }, [Title]);

   useEffect(() => {
      const parsedQuery = qs.parse(location.search);
      const normalizedQuery = Object.keys(parsedQuery)
         .reduce<INormalizedParsedQuery>((destination, key) => {
            destination[key.toLowerCase()] = parsedQuery[key];
            return destination;
         }, {});

      if (normalizedQuery[isCompleteRecordingSessionKey] === 'true') {
         setShowEditDetailsModal(true);
      }

      removeIsCompleteRecordingSessionFromQueryString(normalizedQuery);
   }, []);

   useEffect(() => {
      if (view === 'editDetails' && config.watchConfig.isOwner) {
         setShowEditDetailsModal(true);
      }
   }, [view]);

   useEffect(() => {
      if (!mediaDetailsIsExpanded) {
         window.setTimeout(() => {
            detailsContainer.current && UpdateWindowSizingState({mediaDetailsReservedHeight: `${detailsContainer.current.offsetHeight}px`});
         }, mediaDetailsExpansionTransition);
      }
   }, [detailsContainer.current?.offsetHeight, mediaDetailsIsExpanded]);

   const onCreateCollectionClose = () => {
      setShowCreateCollectionModal(false);
      setShowShareToCollectionModal(true);
      updateMediaStateFromServer();
   };

   const onCreateCollectionClick = () => {
      setShowShareToCollectionModal(false);
      setShowCreateCollectionModal(true);
   };

   setupCSSHeightObserver(detailsContainer, cssConstants.variables.mediaDetailsHeight);

   const getEditMediaButton = (): React.JSX.Element => {
      if (mediaSupportAIFeatures()) {
         return (
            <EditMediaButton
               className="edit-details-button"
               onClick={() => setShowEditDetailsModal(true)}
               disabled={!IsReady}
               isDisabled={!IsReady}
               theme={theme}
            >
               <span className="editDetailsGlyph">
                  <Glyphs.Edit16x16 />
               </span>
               <span className="button-label">
                  {t('EditDetails')}
               </span>
               <div className="ai-glyph">
                  <SparkleGlyph />
               </div>
            </EditMediaButton>
         );
      }
      return (
         <ControlButton
            onClick={() => setShowEditDetailsModal(true)}
            buttonType={TscButtonType.tertiary}
            themeName={theme}
            glyph={<Glyphs.Edit16x16 />}
            title={t('EditDetails')}
            label={t('EditDetails')}
            visible={config.watchConfig.isOwnerAdminOrTeamEditor}
            testId="edit-details-button"
            disabled={!IsReady}
         />
      );
   };

   const resourceLinkId = new URLSearchParams(window.location.search).get('resourceLinkId') || new URLSearchParams(window.location.search).get('resourcelinkid');
   const enableChangeMedia = config.environmentData.IsLti && (config.watchConfig.isOwnerAdminOrTeamEditor) && IsReady && resourceLinkId && config.environmentData.SupportsChangeMedia;
   const displayShareMediaOptions = !config.environmentData.IsLti && (config.watchConfig.isOwnerAdminOrTeamEditor || (config.watchConfig.viewersCanShareMedia && config.user.IsSignedIn));

   return (
      <MediaDetailsContainer
         ref={detailsContainer}
         theme={theme}
      >
         {showMediaLocation && (
            <MediaSpacedBar>
               <MediaLocation />
            </MediaSpacedBar>
         )}
         <MediaSpacedBar>
            <MediaTitle text={Title} />
            <MediaActions>
               <MediaActionWrapper mediaTabsPanelOpen={mediaTabsPanelOpen}>
                  {
                     (config.watchConfig.isOwnerAdminOrTeamEditor) && getEditMediaButton()
                  }
                  {showEditDetailsModal && <EditDetailsModal onClose={() => setShowEditDetailsModal(false)} />}
                  {displayShareMediaOptions && (
                     <>
                        <ControlButton
                           buttonType={TscButtonType.tertiary}
                           themeName={theme}
                           glyph={<Glyphs.ShareToCollection16x16 />}
                           title={t('collections.share.header')}
                           label={t('collections.share.header')}
                           testId="share-to-collection-button"
                           onClick={() => setShowShareToCollectionModal(true)}
                           disabled={migrationInProgress}
                        />
                        {showShareToCollectionModal && (
                           <ShareToCollectionModal
                              onCreateCollection={onCreateCollectionClick}
                              onClose={() => {
                                 setShowShareToCollectionModal(false);
                              }}
                           />
                        )}
                        {showCreateCollectionModal && (
                           <CreateCollectionModal
                              onClose={onCreateCollectionClose}
                              shareMediaToCollection={true}
                           />
                        )}
                     </>
                  )}
                  {enableChangeMedia && (
                     <ControlButton
                        buttonType={TscButtonType.primary}
                        themeName={theme}
                        glyph={<Glyphs.Undo16x16 />}
                        title={t('general.changeMedia')}
                        label={t('general.changeMedia')}
                        href={Constants.navigation.changeMediaLink.replace('{resourceLinkId}', resourceLinkId)}
                        testId="change-media-button"
                     />
                  )}
                  {IsReady && <MoreMenu />}
                  <ControlButton
                     buttonType={TscButtonType.primary}
                     themeName={theme}
                     glyph={<Glyphs.Share16x16 />}
                     title={t('share.action')}
                     label={t('share.action')}
                     testId="share-media-button"
                     onClick={() => {
                        setShowShareModal(true);
                     }}
                  />
                  {showShareModal && (
                     <ShareModal
                        mediaLibraryType={OwnerLibrary.LibraryType}
                        onClose={() => {
                           setShowShareModal(false);
                        }}
                     />
                  )}
               </MediaActionWrapper>
            </MediaActions>
         </MediaSpacedBar>
      </MediaDetailsContainer>
   );
};

export interface IMediaDetailsProps {
   showMediaLocation: boolean;
}

export interface IStateMappedProps {
   mediaDetailsIsExpanded: boolean;
   mediaTabsPanelOpen: boolean;
   Title: string;
   IsReady: boolean;
   IsVideo: boolean;
   OwnerLibrary: IBaseLibrary;
   migrationInProgress: boolean;
}

const mapStatesToProps = (
   {showMediaLocation}: Partial<IMediaDetailsProps>,
   {mediaDetailsIsExpanded}: Partial<IStateMappedProps>,
   {mediaTabsPanelOpen}: Partial<IStateMappedProps>,
   {Title, IsReady, IsVideo, OwnerLibrary}: Partial<IStateMappedProps>,
   {migrationInProgress}: Partial<IStateMappedProps>
): IMediaDetailsProps & IStateMappedProps => ({showMediaLocation, mediaDetailsIsExpanded, mediaTabsPanelOpen, Title, IsReady, IsVideo, OwnerLibrary, migrationInProgress});

export default withMemoizedContexts(mapStatesToProps, windowSizingStore, conversationsStore, mediaStore, migrationStore)(MediaDetailsBase);
